import React from "react";
import LoadingSpinner from "./LoadingSpinner";

type Variant = "primary" | "secondary" | "danger" | "dark";

interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: Variant;
  // Shows an inline spinner and disables the button. Pass `loadingLabel` to
  // swap the text while in flight ("Saving..."); otherwise the label stays.
  loading?: boolean;
  loadingLabel?: React.ReactNode;
  // Stretch to the container - stacked confirm dialogs
  fullWidth?: boolean;
  // Rare per-call additions (e.g. an icon's spacing); never the base style
  className?: string;
}

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50";

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-biafit-primary text-white hover:bg-biafit-secondary focus:ring-biafit-primary",
  secondary:
    "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600",
  danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-600",
  dark: "bg-navy-700 text-white hover:bg-navy-800 focus:ring-navy-700 dark:bg-navy-600 dark:hover:bg-navy-700",
};

// The dashboard's action button. Modal footers (Cancel / Save / Delete)
// all render through this so a spinner never wraps to its own line and no
// two modals style the same button differently.
const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  loading = false,
  loadingLabel,
  fullWidth = false,
  className = "",
  type = "button",
  disabled,
  children,
  ...rest
}) => (
  <button
    type={type}
    disabled={disabled || loading}
    className={`${BASE} ${VARIANT[variant]} ${
      fullWidth ? "w-full" : ""
    } ${className}`}
    {...rest}
  >
    {loading && <LoadingSpinner bare size="xs" tone="white" />}
    {loading && loadingLabel !== undefined ? loadingLabel : children}
  </button>
);

export default Button;
